import React from 'react'
import { useMemo, useRef, useEffect } from 'sb-hooks'
import useGlobals from 'sb-hooks/useGlobals'
import sentry from 'common/helpers/analytics/sentry'
import cx from 'classnames'

import imageSizes from 'decorators/imageSizes'

import s from './Image.scss'


export type ImageProps = {
  className?: string
  src: string
  srcSet?: string
  w?: string | number
  h?: string | number
  quality?: number
  x2?: boolean
  x3?: boolean
  resizeBy: 'width' | 'height'
  placeholderWidth?: number // possible to be passed from !!src-loader
  placeholderHeight?: number // possible to be passed from !!src-loader
  alt?: string
  title?: string
  fullWidth?: boolean
  draggable?: boolean
  itemProp?: string
  dataTestId?: string
  onError?: React.HTMLAttributes<HTMLImageElement>['onError']
}

// Returns svg placeholder with specific size
const getPlaceholder = (width = 1, height = 1) => `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3e%3c/svg%3e`

export const ZinaImage: React.FunctionComponent<ImageProps> = (props) => {
  let {
    className, src, srcSet, h, resizeBy, alt = '',
    placeholderWidth, placeholderHeight,
    title, fullWidth, draggable, itemProp, dataTestId, onError,
  } = props

  const ref = useRef<HTMLImageElement>(null)

  // TODO instead of "null" add ?placeholder here - added on 1/13/20 by pavelivanov
  // const initialSrc = placeholderWidth && placeholderHeight ? getPlaceholder(placeholderWidth, placeholderHeight) : null
  let initialSrc = getPlaceholder(placeholderWidth, placeholderHeight)

  if (typeof window !== 'undefined' && window.location && window.location.search === '?foo=1') {
    initialSrc = undefined
  }

  const zinaSrc = useMemo(() => {
    let zinaSrc = src

    if (srcSet) {
      try {
        zinaSrc = srcSet.trim().match(/([^\s,]+) [0-9]x$/)[1] // image@3x.jpg
      }
      catch (err) {
        console.log(err)
      }
    }

    return zinaSrc
  }, [ src, srcSet ])

  const zinaResizeBy = resizeBy || (h ? 'height' : 'width')

  useEffect(() => {
    const node = ref.current

    if (node) {
      if (!node.src || node.src === 'undefined') {
        sentry.captureMessage('Image:before src is "undefined" 27/02/20', {
          extra: {
            props,
            zinaExists: Boolean(window.zina),
            typeOfSrc: typeof node.src,
            nodeSrc: node.src,
            nodeAlt: node.alt,
            nodeData: node.dataset,
            zinaSrc,
          },
        })
      }

      if (window.zina) {
        window.zina.process(node)
      }
      else {
        node.src = src
        node.srcset = srcSet
      }

      if (!node.src || node.src === 'undefined') {
        sentry.captureMessage('Image:after src is "undefined" 27/02/20', {
          extra: {
            props,
            zinaExists: Boolean(window.zina),
            typeOfSrc: typeof node.src,
            nodeSrc: node.src,
            nodeAlt: node.alt,
            nodeData: node.dataset,
            zinaSrc,
          },
        })
      }
    }
  }, [ src, srcSet, zinaSrc, zinaResizeBy ])

  const rootClassName = cx(className, {
    [s.fullWidth]: fullWidth,
  })

  return (
    <img
      className={rootClassName}
      ref={ref}
      src={initialSrc}
      alt={alt}
      title={title}
      itemProp={itemProp}
      draggable={draggable}
      onError={onError}
      data-zina-src={zinaSrc}
      data-zina-resize-by={zinaResizeBy}
      data-testid={dataTestId}
    />
  )
}

const OldImage: React.FunctionComponent<ImageProps> = imageSizes()((props) => {
  const {
    className, src, srcSet, alt = '',
    title, fullWidth, draggable, itemProp, dataTestId, onError,
  } = props

  const rootClassName = cx(className, {
    [s.fullWidth]: fullWidth,
  })

  return (
    <img
      className={rootClassName}
      src={src}
      srcSet={srcSet}
      alt={alt}
      title={title}
      itemProp={itemProp}
      draggable={draggable}
      onError={onError}
      data-testid={dataTestId}
    />
  )
})

OldImage.displayName = 'OldImage'


const Image: React.FunctionComponent<ImageProps> = (props) => {
  const { w, h, resizeBy } = props

  const { isZinaEnabled } = useGlobals(({ globals: { isZinaEnabled } }) => ({ isZinaEnabled }))

  if (isZinaEnabled && (w || h || resizeBy)) {
    return <ZinaImage {...props} />
  }

  return <OldImage {...props} />
}


export default Image
